import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Plane, BookOpen, Send, Menu, X } from 'lucide-react';

const VisionPage = () => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const navigate = useNavigate();

  return (
  <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="vision-page">
    <nav className="w-full bg-white/95 backdrop-blur-md" style={{ borderBottom: '1px solid #E8E0D4' }}>
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 no-underline">
          <img src="/maluti-icon.png" alt="Maluti Holdings" className="w-7 h-7 object-contain" />
          <span className="text-sm font-semibold tracking-[0.2em] uppercase" style={{ color: '#2C1A0E' }}>Maluti</span>
          <span className="text-sm font-light tracking-[0.2em] uppercase" style={{ color: '#B8860B' }}>Holdings</span>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="/#portfolio" className="text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Portfolio</a>
          <span className="text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: '#2C1A0E' }}>Vision</span>
          <Link to="/advisory" className="text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Speaking</Link>
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-medium transition-all duration-300 hover:opacity-90" style={{ backgroundColor: '#8B7355', color: '#F5F0E8' }}>Ops Suite</button>
        </div>
        <button className="md:hidden p-1" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
          {menuOpen ? <X className="w-6 h-6" style={{ color: '#2C1A0E' }} /> : <Menu className="w-6 h-6" style={{ color: '#2C1A0E' }} />}
        </button>
      </div>
      {menuOpen && (
        <div className="md:hidden px-6 pb-4 bg-white flex flex-col gap-3" style={{ borderTop: '1px solid #E8E0D4' }}>
          <a href="/#portfolio" onClick={() => setMenuOpen(false)} className="text-xs uppercase tracking-[0.15em] py-2 transition-colors hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Portfolio</a>
          <span className="text-xs uppercase tracking-[0.15em] py-2 font-semibold" style={{ color: '#2C1A0E' }}>Vision</span>
          <Link to="/advisory" onClick={() => setMenuOpen(false)} className="text-xs uppercase tracking-[0.15em] py-2 transition-colors hover:text-[#B8860B] no-underline" style={{ color: '#8B7355' }}>Speaking & Advisory</Link>
          <button onClick={() => { navigate('/dashboard'); setMenuOpen(false); }} className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-medium self-start" style={{ backgroundColor: '#8B7355', color: '#F5F0E8' }}>Ops Suite</button>
        </div>
      )}
    </nav>

    {/* Hero — matches homepage white bg with gold accents */}
    <section className="py-10 sm:py-12 px-6 bg-white">
      <div className="max-w-4xl mx-auto text-center">
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="w-12 h-px" style={{ backgroundColor: '#B8860B' }} />
          <span className="text-[11px] uppercase tracking-[0.3em] font-semibold" style={{ color: '#B8860B' }}>The Vision</span>
          <div className="w-12 h-px" style={{ backgroundColor: '#B8860B' }} />
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light mb-6 tracking-tight" style={{ color: '#2C1A0E' }}>
          Maluti Holdings
        </h1>
        <p className="text-sm sm:text-base leading-[1.8] max-w-2xl mx-auto mb-4" style={{ color: '#6B5B4E' }}>
          A growing portfolio spanning trade, travel, creative access platforms and SaaS technology.
        </p>
        <p className="text-sm sm:text-base font-semibold tracking-wide" style={{ color: '#2C1A0E' }}>
          Rooted in Southern Africa. Built for Global Relevance.
        </p>
      </div>
    </section>

    {/* Founder strip — same as homepage */}
    <div className="py-3 px-6" style={{ backgroundColor: '#2C1A0E' }}>
      <div className="max-w-5xl mx-auto text-center">
        <p className="text-xs uppercase tracking-[0.25em]" style={{ color: '#D4AF37' }}>Building businesses that matter — across Southern Africa and the UK</p>
      </div>
    </div>

    {/* Body — white bg matching homepage */}
    <section className="py-10 px-6 bg-white">
      <div className="max-w-5xl mx-auto">
        {/* Founder Quote — full width above grid */}
        <div className="p-5 mb-8 border-l-2" style={{ borderColor: '#B8860B', backgroundColor: '#FAF7F2' }}>
          <p className="text-xs leading-relaxed" style={{ color: '#6B5B4E' }}>
            <span style={{ color: '#D4AF37' }}>&ldquo;</span>We started by helping organisations connect with Southern African talent. Over the years, every project exposed another barrier—finding trusted suppliers, navigating exports, building buyer confidence, creating authentic experiences. Instead of treating those as separate businesses, we realised they were all pieces of the same puzzle. So we built an ecosystem around them.<span style={{ color: '#D4AF37' }}>&rdquo;</span>
          </p>
          <p className="text-xs mt-2" style={{ color: '#B8860B' }}>— Naledi, Ecosystem Architect</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="flex flex-col">
            <p className="text-sm leading-[1.8] mb-5" style={{ color: '#6B5B4E' }}>
              Driven by global connection, our businesses are designed to expand access, strengthen relationships and create lasting value across markets and communities. Spanning curated trade infrastructure, luxe sustainable travel, creative access platforms and modern family technology, each venture is built to unlock opportunity and support long-term growth.
            </p>
            <p className="text-sm leading-[1.8] mb-6" style={{ color: '#6B5B4E' }}>
              Inspired by the founder's deep connection to the Maluti Mountains of Lesotho, Maluti Holdings reflects heritage, resilience and elevated vision.
            </p>
            {/* Framed mountain image */}
            <div className="flex-1 p-2 rounded-lg" style={{ border: '2px solid #D4AF37', backgroundColor: '#FAF7F2' }}>
              <div className="rounded overflow-hidden relative h-full min-h-[200px]">
                <img src="https://images.unsplash.com/photo-1736967462206-8be8ad54063e?w=800&q=80" alt="View from the Maluti Drakensberg summit" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-3 left-4 flex items-center gap-2">
                  <img src="/maluti-icon.png" alt="Maluti Holdings" className="w-7 h-7 object-contain" />
                  <div>
                    <span className="text-xs font-semibold tracking-[0.15em] uppercase" style={{ color: '#FFFFFF' }}>Maluti </span>
                    <span className="text-xs font-light tracking-[0.15em] uppercase" style={{ color: '#D4AF37' }}>Holdings</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {/* Supply Chain — aligned with mountain image */}
            <div className="p-5 border-l-2" style={{ borderColor: '#B8860B', backgroundColor: '#FAF7F2' }}>
              <div className="mb-3" style={{ color: '#B8860B' }}><MapPin className="w-5 h-5" /></div>
              <h3 className="font-medium mb-1.5 text-xs uppercase tracking-[0.15em]" style={{ color: '#2C1A0E' }}>Supply Chain & Trade</h3>
              <p className="text-xs leading-relaxed" style={{ color: '#6B5B4E' }}>
                Resilient supply chains connecting Southern African producers to the global market through the Spaza Global Marketplace and MAPS.
              </p>
              <a href="https://spazaglobal.com/trade-expeditions" target="_blank" rel="noopener noreferrer" className="inline-block text-xs font-medium mt-2 no-underline hover:underline" style={{ color: '#B8860B' }} data-testid="maps-pilot-link">
                The MAPS pilot — connecting local trade with global markets &rarr;
              </a>
            </div>
            {[
              { icon: <Plane className="w-5 h-5" />, title: 'Luxury & Sustainable Travel', text: 'Community-led escapes in Lesotho & South Africa — from leisure to curated trade itineraries', color: '#B8860B' },
              { icon: <BookOpen className="w-5 h-5" />, title: 'Publishing & Fintech', text: 'Amplifying diaspora voices and enabling seamless SA–UK cross-border remittance', color: '#3B6CB0' },
              { icon: <Send className="w-5 h-5" />, title: 'Tech & Connection', text: 'SaaS platforms connecting families across the UK through curated experiences', color: '#C060A0' },
            ].map(({ icon, title, text, color }) => (
              <div key={title} className="p-5 border-l-2" style={{ borderColor: color, backgroundColor: '#FAF7F2' }}>
                <div className="mb-3" style={{ color }}>{icon}</div>
                <h3 className="font-medium mb-1.5 text-xs uppercase tracking-[0.15em]" style={{ color: '#2C1A0E' }}>{title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: '#6B5B4E' }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <footer className="py-8 px-6" style={{ backgroundColor: '#2C1A0E' }}>
      <p className="text-xs text-center" style={{ color: '#8B7355' }}>&copy; {new Date().getFullYear()} Maluti Holdings &middot; hello@malutiholdings.com</p>
    </footer>
  </div>
  );
};

export default VisionPage;
